import { ulid } from './ulid'

/**
 * A stable name for this device, so signing in here does not sign you out
 * everywhere else.
 *
 * The server drops old tokens sharing a name, which is right — a re-login on
 * the same phone should replace that phone's token rather than pile up another
 * one. It only works if the name actually identifies a device, and the client
 * was sending nothing at all, so every device in the plant logged in as `web`.
 * A supervisor opening the app on their phone silently killed their own desktop
 * session, and the desktop then failed its next request with a 401 that looked
 * like the server had gone wrong.
 *
 * Two parts, and both are needed:
 *
 *   - a **label**, because this is what the user reads on the sessions list in
 *     their profile when deciding which one to end. "Chrome · Windows" is a
 *     thing somebody can recognise; a bare id is not.
 *   - a **random suffix**, because labels are not unique. Two identical phones
 *     on the same shift would otherwise share a name and evict each other,
 *     which is the original bug with extra steps.
 */

const DEVICE_KEY = 'kapro.device'

/**
 * The persistent half.
 *
 * localStorage rather than sessionStorage: the point is to survive closing the
 * browser, which is exactly when sessionStorage is discarded. If it is
 * unavailable — private mode in some engines — a fresh id per page load is a
 * worse but safe outcome: extra entries in the sessions list, never a session
 * silently ended somewhere else.
 */
function deviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_KEY)
    if (existing) return existing

    const created = ulid().slice(-6).toLowerCase()
    localStorage.setItem(DEVICE_KEY, created)

    return created
  } catch {
    return ulid().slice(-6).toLowerCase()
  }
}

/** Rough, and deliberately so — see `deviceName`. */
function describeBrowser(ua: string): string {
  if (/\bEdg\//.test(ua)) return 'Edge'
  if (/\bOPR\/|\bOpera\b/.test(ua)) return 'Opera'
  if (/\bFirefox\//.test(ua)) return 'Firefox'
  // Order matters: Chrome's UA contains "Safari", so Chrome must be tested first.
  if (/\bChrome\/|\bCriOS\//.test(ua)) return 'Chrome'
  if (/\bSafari\//.test(ua)) return 'Safari'

  return 'pregledač'
}

function describePlatform(ua: string): string {
  if (/\biPhone\b/.test(ua)) return 'iPhone'
  if (/\biPad\b/.test(ua)) return 'iPad'
  if (/\bAndroid\b/.test(ua)) return 'Android'
  if (/\bWindows\b/.test(ua)) return 'Windows'
  if (/\bMac OS X\b/.test(ua)) return 'Mac'
  if (/\bLinux\b/.test(ua)) return 'Linux'

  return 'uređaj'
}

/**
 * What the server stores as the token name, and what the user sees listed.
 *
 * The user-agent parsing is deliberately shallow. It decorates a label a person
 * reads; nothing depends on it being right, and the random suffix carries the
 * identity. Guessing wrong costs a slightly odd line in a list, which is a much
 * better failure than the alternative — and far better than shipping a UA
 * parsing library to be exact about a decoration.
 *
 * Capped at 100 characters to match the server's validation rule.
 */
export function deviceName(): string {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent

  return `${describeBrowser(ua)} · ${describePlatform(ua)} · ${deviceId()}`.slice(0, 100)
}
